import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { printDocumentAsPdf, printNoteAsPdf, printDigitalizedDocument } from 'modules/digitalization/actions';
import PayCashApi from 'modules/payCash/repository';
import BancomerApi from 'modules/bancomer/repository';
import PuntoredApi from 'modules/puntored/repository';
import EmbedPdf from 'components/EmbedPdf';
import Modal from 'components/Modal';
import Button from 'components/UI/Button';
import useLiterals from 'utils/hooks/useLiterals';
import { ReactComponent as IconDownload } from 'assets/icons/download-white.svg';
import InlineError from 'components/UI/InlineError';

const DocumentViewer = ({
  documentId,
  documentType,
  noteId,
  path,
  externalOrder,
  onClose,
}) => {
  const literals = useLiterals('digitalization');
  const dispatch = useDispatch();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    let fetchPdf;
    if (externalOrder) {
      fetchPdf = (async () => {
        if (externalOrder.startsWith('MPMP')) {
          return BancomerApi.getBancomerPDF(externalOrder);
        }
        if (externalOrder.startsWith('MPP')) {
          return PayCashApi.getPaycashPDF(externalOrder);
        }
        if (externalOrder.startsWith('MPB')) {
          return BancomerApi.getBancomerPDF(externalOrder);
        }
        if (externalOrder.startsWith('MPR')) {
          return PuntoredApi.getPDF(externalOrder);
        }
        throw new Error('Unknown external order type');
      })();
    } else if (noteId) {
      fetchPdf = dispatch(printNoteAsPdf(noteId));
    } else if (path) {
      fetchPdf = dispatch(printDigitalizedDocument(path));
    } else {
      fetchPdf = dispatch(printDocumentAsPdf(documentType, documentId));
    }

    fetchPdf
      .then((blob) => {
        if (blob && blob.size > 0) {
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
        } else {
          setError(literals.errorLoadDocument);
        }
      })
      .catch(() => {
        setError(literals.errorLoadDocument);
      })
      .finally(() => setLoading(false));

    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const getDownloadFilename = () => {
    if (externalOrder) {
      return `comprobante-${externalOrder}.pdf`;
    }
    if (noteId) {
      return `comprobante-${noteId}.pdf`;
    }
    if (path) {
      const parts = path.split('/');
      return parts[parts.length - 1] || 'documento.pdf';
    }
    return `comprobante-${documentId}.pdf`;
  };

  const handleDownload = () => {
    if (pdfUrl) {
      fetch(pdfUrl)
        .then((res) => res.blob())
        .then((blob) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64 = reader.result.split(',')[1];
            window.saveBlobFile(base64, 'application/pdf', getDownloadFilename());
          };
          reader.readAsDataURL(blob);
        });
    }
  };

  const viewerTitle = (() => {
    if (externalOrder) return `${literals.document}: ${externalOrder}`;
    if (noteId) return `${literals.document}: ${noteId}`;
    if (documentId) return `${literals.document}: ${documentId}`;
    return literals.document;
  })();

  return (
    <Modal
      showCloseButton
      onClose={onClose}
      title={viewerTitle}
      fullScreen
      closeOnBackgroundClick={false}
    >
      <div className='document-viewer'>
        {loading && (
          <div className='document-viewer__loading'>
            <p>{literals.loadingDocument}</p>
          </div>
        )}
        {error && (
          <div className='document-viewer__error'>
            <InlineError error={error} />
            <Button type='secondary' onClick={onClose} text={literals.close} />
          </div>
        )}
        {pdfUrl && !loading && !error && (
          <>
            <EmbedPdf
              src={pdfUrl}
              height='lg'
            />
            <div className='document-viewer__actions'>
              <Button type='primary' onClick={handleDownload}>
                <IconDownload width={18} height={18} color='white' fill='white' />
                <span className='ms-2'>{literals.download}</span>
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

DocumentViewer.propTypes = {
  documentId: PropTypes.string,
  documentType: PropTypes.number,
  noteId: PropTypes.string,
  path: PropTypes.string,
  externalOrder: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

DocumentViewer.defaultProps = {
  documentId: null,
  documentType: null,
  noteId: null,
  path: null,
  externalOrder: null,
};

export default DocumentViewer;
